import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

import { auth, db } from "../firebase";
import { generateCardNumber } from "../utils/cardNumber";
import "./AdminCards.css";

function AdminCards() {
  const navigate = useNavigate();

  const [batchName, setBatchName] = useState("");
  const [quantity, setQuantity] = useState(50);
  const [notes, setNotes] = useState("");

  const [generating, setGenerating] = useState(false);
  const [generatedCards, setGeneratedCards] = useState([]);

  const [latestCards, setLatestCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const cardsQuery = query(
      collection(db, "cards"),
      orderBy("createdAt", "desc"),
      limit(200),
    );

    const unsubscribe = onSnapshot(
      cardsQuery,
      (snapshot) => {
        const cards = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setLatestCards(cards);
        setLoadingCards(false);
      },
      (error) => {
        console.error("Error loading cards:", error);
        setLoadingCards(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const filteredCards = useMemo(() => {
    let result = latestCards;

    if (statusFilter !== "all") {
      result = result.filter((card) => card.status === statusFilter);
    }

    if (searchText.trim()) {
      const search = searchText.trim().toLowerCase();

      result = result.filter((card) => {
        const cardNumber = card.cardNumber?.toLowerCase() || "";
        const batch = card.batchName?.toLowerCase() || "";
        const status = card.status?.toLowerCase() || "";

        return (
          cardNumber.includes(search) ||
          batch.includes(search) ||
          status.includes(search)
        );
      });
    }

    return result;
  }, [latestCards, searchText, statusFilter]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "Not available";

    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

      return date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Not available";
    }
  };

  const generateUniqueCardNumber = async (usedNumbers) => {
    let attempts = 0;

    while (attempts < 100) {
      const cardNumber = generateCardNumber();

      if (usedNumbers.has(cardNumber)) {
        attempts += 1;
        continue;
      }

      const cardRef = doc(db, "cards", cardNumber);
      const cardSnap = await getDoc(cardRef);

      if (!cardSnap.exists()) {
        usedNumbers.add(cardNumber);
        return cardNumber;
      }

      attempts += 1;
    }

    throw new Error("Could not generate a unique card number. Try again.");
  };

  const handleGenerateCards = async (e) => {
    e.preventDefault();

    const cleanBatchName = batchName.trim();
    const cleanNotes = notes.trim();
    const cardQuantity = Number(quantity);

    if (!cleanBatchName) {
      alert("Please enter a batch name.");
      return;
    }

    if (!Number.isInteger(cardQuantity) || cardQuantity < 1) {
      alert("Quantity must be at least 1.");
      return;
    }

    if (cardQuantity > 200) {
      alert("For safety, generate maximum 200 cards at one time.");
      return;
    }

    try {
      setGenerating(true);

      const usedNumbers = new Set(
        latestCards.map((card) => card.cardNumber).filter(Boolean),
      );

      const newCards = [];

      for (let i = 0; i < cardQuantity; i += 1) {
        const uniqueCardNumber = await generateUniqueCardNumber(usedNumbers);
        newCards.push(uniqueCardNumber);
      }

      const batchRef = doc(collection(db, "cardBatches"));
      const firebaseBatch = writeBatch(db);

      firebaseBatch.set(batchRef, {
        batchId: batchRef.id,
        batchName: cleanBatchName,
        quantity: cardQuantity,
        notes: cleanNotes,
        createdByAdminUid: auth.currentUser?.uid || null,
        createdByAdminEmail: auth.currentUser?.email || null,
        createdAt: serverTimestamp(),
      });

      newCards.forEach((cardNumber) => {
        const cardRef = doc(db, "cards", cardNumber);
        const batchNumberRef = doc(
          db,
          "cardBatches",
          batchRef.id,
          "numbers",
          cardNumber,
        );

        const cardData = {
          cardNumber,
          status: "available",
          activated: false,
          activatedAt: null,
          activatedByStoreId: null,
          activatedByStoreUid: null,
          assignedToMemberId: null,
          batchId: batchRef.id,
          batchName: cleanBatchName,
          printed: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        firebaseBatch.set(cardRef, cardData);

        firebaseBatch.set(batchNumberRef, {
          cardNumber,
          status: "available",
          createdAt: serverTimestamp(),
        });
      });

      await firebaseBatch.commit();

      const displayCards = newCards.map((cardNumber) => ({
        cardNumber,
        status: "available",
        batchName: cleanBatchName,
      }));

      setGeneratedCards(displayCards);
      setBatchName("");
      setQuantity(50);
      setNotes("");

      alert(`${cardQuantity} unique cards generated successfully.`);
    } catch (error) {
      console.error("Error generating cards:", error);
      alert("Something went wrong while generating cards.");
    } finally {
      setGenerating(false);
    }
  };

  const downloadCSV = (cardsToDownload, fileName) => {
    if (!cardsToDownload.length) {
      alert("No cards to export.");
      return;
    }

    const escapeCSV = (value) => {
      const cleanValue = String(value ?? "").replace(/"/g, '""');
      return `"${cleanValue}"`;
    };

    const excelTextNumber = (value) => {
      const cleanValue = String(value ?? "").replace(/"/g, '""');
      return `"=""${cleanValue}"""`;
    };

    const headers = [
      "cardNumber",
      "status",
      "batchName",
      "activated",
      "printed",
    ];

    const rows = cardsToDownload.map((card) => [
      excelTextNumber(card.cardNumber),
      escapeCSV(card.status || ""),
      escapeCSV(card.batchName || ""),
      escapeCSV(card.activated === true ? "yes" : "no"),
      escapeCSV(card.printed === true ? "yes" : "no"),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cards-page">
      <div className="cards-topbar">
        <div>
          <h1>Card Inventory</h1>
          <p>Generate secure random card numbers for PointsHub cards.</p>
        </div>

        <div className="cards-topbar-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate("/dashboard")}
          >
            Dashboard
          </button>
        </div>
      </div>

      <div className="cards-layout">
        <section className="cards-panel">
          <h2>Generate New Cards</h2>
          <p className="panel-subtitle">
            These numbers will be saved in Firestore and checked for duplicates
            before creating.
          </p>

          <form onSubmit={handleGenerateCards} className="generate-form">
            <label>
              Batch Name
              <input
                type="text"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="Example: First 100 cards"
              />
            </label>

            <label>
              Quantity
              <input
                type="number"
                min="1"
                max="200"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>

            <label>
              Notes
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Example: Cards for first printing batch"
                rows="3"
              />
            </label>

            <button type="submit" className="primary-btn" disabled={generating}>
              {generating ? "Generating..." : "Generate Cards"}
            </button>
          </form>
        </section>

        <section className="cards-panel">
          <div className="panel-header-row">
            <div>
              <h2>Last Generated Batch</h2>
              <p className="panel-subtitle">
                Export this CSV for printing, barcode, or magnetic stripe
                encoding.
              </p>
            </div>

            <button
              type="button"
              className="secondary-btn"
              onClick={() =>
                downloadCSV(generatedCards, "pointshub-generated-cards.csv")
              }
            >
              Export CSV
            </button>
          </div>

          {generatedCards.length === 0 ? (
            <div className="empty-box">
              No cards generated in this session yet.
            </div>
          ) : (
            <div className="generated-list">
              {generatedCards.map((card) => (
                <div className="generated-card-row" key={card.cardNumber}>
                  <span>{card.cardNumber}</span>
                  <strong>{card.status}</strong>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="cards-panel full-panel">
        <div className="panel-header-row">
          <div>
            <h2>Latest Cards</h2>
            <p className="panel-subtitle">
              Showing latest 200 cards from Firestore.
            </p>
          </div>

          <button
            type="button"
            className="secondary-btn"
            onClick={() => downloadCSV(filteredCards, "pointshub-cards.csv")}
          >
            Export Visible
          </button>
        </div>

        <div className="cards-filters">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search card number, batch, or status"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="printed">Printed</option>
            <option value="activated">Activated</option>
            <option value="blocked">Blocked</option>
            <option value="lost">Lost</option>
          </select>
        </div>

        <div className="cards-table-wrap">
          <table className="cards-table">
            <thead>
              <tr>
                <th>Card Number</th>
                <th>Status</th>
                <th>Activated</th>
                <th>Printed</th>
                <th>Batch</th>
                <th>Created</th>
              </tr>
            </thead>

            <tbody>
              {loadingCards ? (
                <tr>
                  <td colSpan="6">Loading cards...</td>
                </tr>
              ) : filteredCards.length === 0 ? (
                <tr>
                  <td colSpan="6">No cards found.</td>
                </tr>
              ) : (
                filteredCards.map((card) => (
                  <tr key={card.id}>
                    <td className="card-number-cell">{card.cardNumber}</td>
                    <td>
                      <span className={`card-status ${card.status}`}>
                        {card.status || "available"}
                      </span>
                    </td>
                    <td>{card.activated === true ? "Yes" : "No"} </td>
                    <td>{card.printed === true ? "Yes" : "No"}</td>
                    <td>{card.batchName || "Not available"}</td>
                    <td>{formatDate(card.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminCards;
